/** Shared WBS/Gantt splitter widths. Drag snaps to column edges; double-click toggles min ↔ last column. */

export const NAME_MIN_WIDTH = 200;
export const GRID_MAX_WIDTH = 1600;
export const GRID_DEFAULT_WIDTH = 720;
/** 1px grid container border counted in `gridWidth`. */
export const GRID_BORDER_PX = 1;
/** Arrow-key nudge when already past the last column. */
export const GRID_NUDGE_PX = 16;
/** Ignore tiny pointer travel so a double-click is not treated as a drag. */
export const GRID_DRAG_THRESHOLD_PX = 4;

export const IDENTITY_COL_WIDTH = {
  row: 32,
  wbs: 56,
} as const;

/** Near-enough to treat a dragged width as parked at an extreme. */
const SNAP_EPS = 8;

export interface GridColSpec {
  key: string;
  width: number;
}

export function gridMinWidth(hiddenColumns: readonly string[] = []): number {
  let width = IDENTITY_COL_WIDTH.row + NAME_MIN_WIDTH + GRID_BORDER_PX;
  if (!hiddenColumns.includes("wbs")) width += IDENTITY_COL_WIDTH.wbs;
  return width;
}

export function clampGridWidth(width: number, minWidth = gridMinWidth()): number {
  if (!Number.isFinite(width)) return GRID_DEFAULT_WIDTH;
  return Math.max(minWidth, Math.min(GRID_MAX_WIDTH, width));
}

export function isGridWidthAt(width: number, target: number): boolean {
  return Math.abs(width - target) <= SNAP_EPS;
}

/** Splitter stops at the end of the name column and each following visible column. */
export function gridColumnStops(
  cols: readonly GridColSpec[],
  hiddenColumns: readonly string[] = [],
  nameWidth = NAME_MIN_WIDTH,
): number[] {
  const minWidth = gridMinWidth(hiddenColumns);
  let acc = GRID_BORDER_PX;
  const stops: number[] = [];
  for (const col of cols) {
    if (hiddenColumns.includes(col.key)) continue;
    acc += col.key === "name" ? nameWidth : col.width;
    if (acc >= minWidth) stops.push(clampGridWidth(acc, minWidth));
  }
  return [...new Set(stops)];
}

export function gridFullWidth(stops: readonly number[], minWidth = gridMinWidth()): number {
  return stops[stops.length - 1] ?? minWidth;
}

/** Width that shows every enabled column at its defined size (name at the floor). */
export function gridWidthForColumns(
  cols: readonly GridColSpec[],
  hiddenColumns: readonly string[] = [],
): number {
  const minWidth = gridMinWidth(hiddenColumns);
  return gridFullWidth(gridColumnStops(cols, hiddenColumns), minWidth);
}

/** When columns are turned on, grow the pane so the new fields are not clipped. */
export function nextGridWidthForHiddenColumns(
  currentWidth: number,
  cols: readonly GridColSpec[],
  hiddenColumns: readonly string[],
): number {
  const minWidth = gridMinWidth(hiddenColumns);
  return clampGridWidth(Math.max(currentWidth, gridWidthForColumns(cols, hiddenColumns)), minWidth);
}

/**
 * Snap to the nearest column edge. Widths past the last column stay put so the name column can grow.
 */
export function snapGridWidthToColumn(
  width: number,
  stops: readonly number[],
  minWidth = gridMinWidth(),
): number {
  const clamped = clampGridWidth(width, minWidth);
  const last = gridFullWidth(stops, minWidth);
  if (clamped > last + SNAP_EPS) return clamped;
  let best = last;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const stop of stops) {
    const snap = clampGridWidth(stop, minWidth);
    const dist = Math.abs(clamped - snap);
    if (dist < bestDist) {
      bestDist = dist;
      best = snap;
    }
  }
  return best;
}

/**
 * Double-click: collapse to the task column, or expand so every enabled column is fully visible.
 */
export function nextGridWidthOnDoubleClick(
  width: number,
  minWidth = gridMinWidth(),
  fullWidth = GRID_DEFAULT_WIDTH,
): number {
  const full = clampGridWidth(fullWidth, minWidth);
  if (isGridWidthAt(minWidth, full)) return minWidth;
  return isGridWidthAt(width, minWidth) ? full : minWidth;
}

export function nextGridWidthOnKey(
  width: number,
  key: string,
  minWidth = gridMinWidth(),
  stops: readonly number[] = [],
): number | null {
  const full = gridFullWidth(stops, minWidth);
  if (key === "Home") return minWidth;
  if (key === "End") return full;
  if (key === "ArrowLeft") return adjacentColumnStop(width, stops, minWidth, -1);
  if (key === "ArrowRight") return adjacentColumnStop(width, stops, minWidth, 1);
  return null;
}

function adjacentColumnStop(
  width: number,
  stops: readonly number[],
  minWidth: number,
  dir: -1 | 1,
): number {
  const last = gridFullWidth(stops, minWidth);
  if (dir > 0 && width > last + SNAP_EPS) return clampGridWidth(width + GRID_NUDGE_PX, minWidth);
  if (dir < 0 && width > last + SNAP_EPS) return last;
  const ordered = [...stops].sort((a, b) => a - b);
  if (dir > 0) {
    const next = ordered.find((stop) => stop > width + SNAP_EPS);
    return next ?? last;
  }
  const prev = [...ordered].reverse().find((stop) => stop < width - SNAP_EPS);
  return prev ?? minWidth;
}
